import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Button } from 'src/domains/layouts/webview/components/common/button/Button';
import { Input } from 'src/domains/players/webview/components/form/input/Input';
import { withConfig } from 'src/withConfig';
import { buildBtnBackgroundColor } from 'src/domains/layouts/webview/components/common/button/utils';

/** Full-width layout; colours come from Button (ButtonUniversalWrapper) theme defaults. */
export const DepositLimitErrorMessage = styled('span', { label: 'DepositLimitErrorMessage' })`
    display: block;
    flex: 1 1 auto;
    min-width: 0;
`;

export const DepositLimitHelperMailLink = withConfig(theme => styled('a', { label: 'DepositLimitHelperMailLink' })`
    color: inherit;
    display: inline;
    font-weight: ${theme.star.fontWeight.bold};
    overflow-wrap: anywhere;
    text-decoration: underline;
`);

export const ButtonSubmit = styled(Button, { label: 'ButtonSubmit' })`
    display: block;
    width: 100%;
`;

/** Full-width cancel (muted). Uses `buildBtnBackgroundColor` so we avoid deprecated `Button` `version`. */
export const FullWidthSecondaryButton = withConfig(theme => styled(Button, { label: 'FullWidthSecondaryButton' })<{ limitVariant: 'account' | 'blocking' }>`
    border: 0;
    display: block;
    width: 100%;
    ${({ limitVariant }) =>
        limitVariant === 'account'
            ? buildBtnBackgroundColor(theme, 'secondary-light')
            : buildBtnBackgroundColor(theme, 'secondary-dark')};
    box-shadow: none;
    &:hover,
    &:focus,
    &:focus-visible,
    &:active {
        box-shadow: none;
    }
`);

export const CalculationTypeRadioGroup = styled('div', { label: 'CalculationTypeRadioGroup' })`
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
    width: 100%;
`;

export const CalculationTypeRadioCard = withConfig(theme => styled('button', { label: 'CalculationTypeRadioCard' })`
    align-items: center;
    appearance: none;
    background-color: ${theme.star.depositLimits.bgColor};
    border: 0;
    border-radius: ${theme.star.radius.medium};
    color: ${theme.star.depositLimits.txtColor};
    cursor: pointer;
    display: flex;
    flex: 1;
    font: inherit;
    gap: ${theme.star.padding.medium};
    justify-content: space-between;
    min-width: 0;
    padding: ${theme.star.padding.xMedium};
    position: relative;
    text-align: left;
    &:focus,
    &:focus-visible {
        outline: none;
    }
`);

export const CalculationTypeRadioCaption = withConfig(theme => styled('span', { label: 'CalculationTypeRadioCaption' })`
    font-size: ${theme.star.fontSize.small};
    font-weight: ${theme.star.fontWeight.regular};
    line-height: 1.3;
`);

export const CalculationTypeRadioIndicator = withConfig(theme => styled('span', { label: 'CalculationTypeRadioIndicator' })<{ isSelected: boolean }>`
    align-items: center;
    border-radius: 50%;
    display: flex;
    flex-shrink: 0;
    height: 22px;
    justify-content: center;
    width: 22px;
    border: 2px solid ${theme.star.depositLimits.borderColor};
    &::after {
        background-color: ${({ isSelected }): string =>
            isSelected === true
                ? theme.star.statuses.success.bgColor
                : ''};
        border-radius: 50%;
        content: '';

        height: 12px;
        width: 12px;
    }
`);

export const TopUpLimitsInput = styled(Input, { label: 'TopUpLimitsInput' })`
    margin: 0;
`;

export const RollingDepositLimitForm = styled('form', { label: 'RollingDepositLimitForm' })<{ variant: 'account' | 'blocking' }>`
    ${({ variant }) =>
        variant === 'blocking'
            ? `margin: 0; flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;`
            : 'margin: 16px;'}      
`;

export const TextSection = withConfig(theme => styled('p', { label: 'TextSection' })`
    line-height: 1.33;
    margin: 8px 0 24px;
    font-size: ${theme.star.fontSize.xRegular};
`);

export const ReadonlyIntro = styled(TextSection, { label: 'ReadonlyIntro' })`
    margin: 0 0 16px;
    font-size: 14px;
`;

export const TPWrapper = withConfig(theme => styled('div', { label: 'TPWrapper' })`
    display: flex;
    font-size: ${theme.star.fontSize.small};
    justify-content: space-between;
    margin-top: 2px;
    line-height: 1.33;
`);

export const SectionHeader = withConfig(theme => styled('div', { label: 'SectionHeader' })<{ variant: 'account' | 'blocking' }>`
    font-size: ${theme.star.fontSize.small};
    line-height: 1.33;
    color: ${({ variant }) => variant === 'blocking' ? theme.star.depositLimits.txtColorTertiary : theme.star.depositLimits.txtColor};
    font-weight: ${theme.star.fontWeight.medium};
    margin-bottom: 4px;
`);

export const HeaderTitle = withConfig(theme => styled('h4', { label: 'HeaderTitle' })`
    font-weight: ${theme.star.fontWeight.bold};
    margin-bottom: 4px;
    font-size: ${theme.star.fontSize.regular};
`);

/** Edit page title sits outside the form, which already has 16px margin. */
export const EditLimitsTitle = styled(HeaderTitle, { label: 'EditLimitsTitle' })`
    margin: 16px 16px 0;
`;

export const SectionDescritpion = withConfig(theme => styled('p', { label: 'SectionDescritpion' })`
    font-size: ${theme.star.fontSize.xRegular};
    line-height: 1.42;
    margin-bottom: 24px;
`);

export const AmountInputDescription = styled('div', { label: 'AmountInputDescription' })`
    display: flex;
    justify-content: space-between;
    width: 100%;
`;

export const ReadonlyCardAvailableAmountRow = withConfig(theme => styled(AmountInputDescription, { label: 'ReadonlyCardAvailableAmountRow' })<{ isDarkMode?: boolean }>`
    justify-content: flex-end;
    color: ${({ isDarkMode }): string => isDarkMode === true ? theme.star.depositLimits.txtColorQuaternary : theme.star.depositLimits.txtColorSecondary};
`);

export const CalendarLimitAvailableAndResetRow = withConfig(theme => styled('div', { label: 'CalendarLimitAvailableAndResetRow' })<{ isDarkMode?: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    width: 100%;
    color: ${({ isDarkMode }): string =>
        isDarkMode === true ? theme.star.depositLimits.txtColorQuaternary : theme.star.depositLimits.txtColorSecondary};
`);

export const ReadonlyPendingLimitFooter = withConfig(theme => styled('div', { label: 'ReadonlyPendingLimitFooter' })`
    font-size: ${theme.star.fontSize.small};
    font-weight: ${theme.star.fontWeight.regular};
    line-height: 1.35;
    margin-top: 12px;
    text-align: left;
    width: 100%;
`);

export const fadeSlideIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

export const AnimatedFormSection = styled('div', { label: 'AnimatedFormSection' })`
    animation: ${fadeSlideIn} 0.32s ease-out;
`;

export const ReadonlyCardsStack = styled('div', { label: 'ReadonlyCardsStack' })`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
`;

export const ReadonlyCardHeaderRow = styled('div', { label: 'ReadonlyCardHeaderRow' })`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
`;

export const ReadonlyCardPeriod = withConfig(theme => styled('span', { label: 'ReadonlyCardPeriod' })`
    font-weight: ${theme.star.fontWeight.bold};
`);

export const ReadonlyCardAmount = withConfig(theme => styled('span', { label: 'ReadonlyCardAmount' })`
    font-weight: ${theme.star.fontWeight.bold};
`);

export const FormFieldBlock = styled('div', { label: 'FormFieldBlock' })<{isSignup?: boolean}>`
    margin-bottom: ${({ isSignup }): string => isSignup ? '8px' : '16px'};
    margin-top: ${({ isSignup }): string => isSignup ? '8px' : '0'};
`;

export const EditFormActions = styled('div', { label: 'EditFormActions' })`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 24px;
`;

export const LimitsTypeSection = styled('section', { label: 'LimitsTypeSection' })`
    & + & {
        margin-top: 32px;
    }
`;

export const CalculationTypeToggleTrack = withConfig(theme => styled('div', { label: 'CalculationTypeToggleTrack' })`
    background-color: ${theme.star.depositLimits.txtColor};
    border-radius: 999px;
    display: flex;
    gap: 4px;
    margin: 0 0 16px;
    padding: 4px;
    width: 100%;
`);

export const CalculationTypeToggleOption = withConfig(theme => styled('button', { label: 'CalculationTypeToggleOption' })<{ isSelected: boolean }>`
    appearance: none;
    background-color: ${({ isSelected }): string =>
        isSelected === true ? theme.star.depositLimits.bgColor : 'transparent'};
    border: 0;
    border-radius: 999px;
    color: ${({ isSelected }): string =>
        isSelected === true ? theme.star.depositLimits.txtColor : theme.star.depositLimits.txtColorTertiary};
    cursor: pointer;
    flex: 1;
    font-size: ${theme.star.fontSize.small};
    font-weight: ${theme.star.fontWeight.bold};
    line-height: 1.3;
    min-width: 0;
    padding: 10px 12px;
    text-align: center;
    &:focus,
    &:focus-visible {
        outline: none;
    }
`);
